import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components";
import { useSnackBar } from "@/utils/useSnackbar";

import { useTriggerSalesforceResyncMutation } from "@/pages/Private/redux/salesforce/salesforce.api";

/**
 * `SalesforceResyncService.triggerResync` guards **only** against an unconnected company —
 * unlike some of its siblings it has no in-flight guard, so a second press queues a second
 * fan-out and re-charges every already-synced prospect's AI mappings. This cooldown is
 * therefore load-bearing rather than cosmetic; do not remove it as redundant with the API.
 */
const COOLDOWN_MS = 30000;

const getBackendMessage = (error: unknown): string | undefined => {
	if (typeof error === "object" && error !== null && "data" in error) {
		const data = (error as { data?: { message?: string } }).data;

		if (data && typeof data.message === "string") {
			return data.message;
		}
	}

	return undefined;
};

export const SalesforceResyncSection: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.salesforce.resync.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
	const [isCooldown, setIsCooldown] = useState<boolean>(false);

	const [triggerResync, { isLoading }] = useTriggerSalesforceResyncMutation();

	useEffect(() => {
		if (!isCooldown) {
			return;
		}

		const timeout = window.setTimeout(() => setIsCooldown(false), COOLDOWN_MS);

		return () => window.clearTimeout(timeout);
	}, [isCooldown]);

	const handleConfirmResync = async () => {
		try {
			const response = await triggerResync().unwrap();

			setIsConfirmOpen(false);
			setIsCooldown(true);

			if (response.queuedCount === 0) {
				handleOpenSnackbar(ts("empty"));
			} else {
				handleOpenSnackbar(t("company.salesforce.resync.success", { count: response.queuedCount }));
			}
		} catch (error) {
			setIsConfirmOpen(false);
			// The backend's own "not configured" message is more actionable than a generic one.
			handleOpenSnackbar(getBackendMessage(error) ?? ts("error"));
		}
	};

	return (
		<>
			<div className="mt-6 border-t border-border-light pt-6">
				<div className="mb-2 text-sm font-medium text-text">{ts("title")}</div>
				<div className="mb-3 text-sm text-text-meta">{ts("description")}</div>
				<div className="w-fit">
					<Button
						color={ButtonColor.ACTION}
						disabled={isLoading || isCooldown}
						isLoading={isLoading}
						size={ButtonSize.S}
						testId="salesforce-resync-button"
						title={ts("button")}
						onClick={() => setIsConfirmOpen(true)}
					/>
				</div>
			</div>

			{isConfirmOpen && (
				<Modal
					handleClose={() => setIsConfirmOpen(false)}
					handleSave={handleConfirmResync}
					isLoading={isLoading}
					isOpened={isConfirmOpen}
					submitButtonText={ts("confirmCta")}
					title={ts("confirmTitle")}
				>
					<div className="text-sm text-text">{ts("confirmBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
